// Session 34 · P1 — v2 Book Now component.
/* eslint-disable react/no-unescaped-entities */
// Drop-in booking widget: shows a date/time picker + book button, hits
// /api/bookings/v2/checkout, and redirects to Stripe Checkout.
//
// Backend enforces:
//   - unconfigured-workspace block (409)
//   - policy windows (min-notice, max-advance) (400)
//   - Stripe onboarding requirement (503)
//   - atomic double-booking prevention with buffers (409)
//   - idempotency-keyed Checkout Session creation (double-click safe)
import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const HOURS_OPTIONS = [1, 2, 3, 4, 6, 8];
const TIME_OPTIONS = [
  "07:00","08:00","09:00","10:00","11:00","12:00",
  "13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00",
];

function todayIso() {
  const d = new Date();
  d.setDate(d.getDate() + 1); // default: tomorrow
  return d.toISOString().slice(0, 10);
}

export default function BookNowV2({ workspaceId, workspaceName, defaultEmail = "",
                                     defaultName = "", hourly = null, daily = null,
                                     paymentSchedule = null, venueTimezone = null,
                                     onSuccess }) {
  const [date, setDate] = useState(todayIso());
  const [time, setTime] = useState("10:00");
  const [hours, setHours] = useState(2);
  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [settings, setSettings] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [pricingType, setPricingType] = useState("hourly");
  const [depositTerms, setDepositTerms] = useState(null);

  // Load network defaults for pricing preview / disclaimers
  useEffect(() => {
    let cancelled = false;
    api.get("/booking-config/network-defaults")
      .then(r => { if (!cancelled) setSettings(r.data); })
      .catch(() => {});
    api.get(`/pricing/resolved?workspace_id=${encodeURIComponent(workspaceId)}`)
      .then(r => {
        if (cancelled) return;
        setPricing(r.data);
        if (r.data?.hourly_enabled) setPricingType("hourly");
        else if (r.data?.half_day_enabled) setPricingType("half_day");
        else if (r.data?.full_day_enabled) setPricingType("full_day");
      })
      .catch(() => {});
    // Deposit terms: prefer props (passed by SpaceDetail from its own
    // /api/spaces/{slug} fetch). Fall back to nothing — the second fetch
    // in this component used to hit /api/spaces/{workspace_id} which 404s
    // because that endpoint is slug-only.
    if (paymentSchedule?.payment_schedule_type === "deposit_then_balance") {
      setDepositTerms({
        deposit_type: paymentSchedule.deposit_type || "percentage",
        deposit_percentage: paymentSchedule.deposit_percentage,
        deposit_amount: paymentSchedule.deposit_amount,
        balance_due_timing: paymentSchedule.balance_due_timing,
        balance_due_time_local: paymentSchedule.balance_due_time_local,
        balance_due_days_before: paymentSchedule.balance_due_days_before,
        auto_charge_balance: paymentSchedule.auto_charge_balance !== false,
        deposit_refundable_within_hours: paymentSchedule.deposit_refundable_within_hours,
        venue_timezone: venueTimezone || "America/New_York",
      });
    } else {
      setDepositTerms(null);
    }
    return () => { cancelled = true; };
  }, [workspaceId, paymentSchedule, venueTimezone]);

  const effectiveHours = (() => {
    if (pricingType === "half_day" && pricing?.half_day_duration_hours) return pricing.half_day_duration_hours;
    if (pricingType === "full_day" && pricing?.full_day_duration_hours) return pricing.full_day_duration_hours;
    return Number(hours);
  })();

  const estAmount = (() => {
    if (!pricing) {
      if (daily && effectiveHours >= 8) return Math.round(daily * (effectiveHours / 8) * 100) / 100;
      if (hourly) return Math.round(hourly * effectiveHours * 100) / 100;
      return null;
    }
    if (pricingType === "hourly" && pricing.hourly_enabled)
      return Math.round(pricing.hourly_rate * effectiveHours * 100) / 100;
    if (pricingType === "half_day" && pricing.half_day_enabled)
      return pricing.half_day_rate;
    if (pricingType === "full_day" && pricing.full_day_enabled)
      return pricing.full_day_rate;
    return null;
  })();

  const onBook = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError("");
    if (!name.trim() || !email.trim()) { setError("Please enter your name and email."); return; }
    setBusy(true);
    try {
      const r = await api.post("/bookings/v2/checkout", {
        workspace_id: workspaceId,
        date, start_time: time,
        hours: pricingType === "hourly" ? Number(hours) : undefined,
        pricing_type: pricingType,
        name: name.trim(), email: email.trim(),
        phone: phone.trim() || null,
        origin_url: window.location.origin,
      });
      if (r.data?.checkout_url) {
        if (onSuccess) onSuccess(r.data);
        // Redirect user to Stripe Checkout
        window.location.assign(r.data.checkout_url);
      } else {
        setError("No checkout URL returned by server.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Booking failed. Please try again.";
      setError(detail);
      // 409 conflict → user-friendly toast so they know why
      if (err?.response?.status === 409) {
        toast.error(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onBook} className="m-card" style={{ padding: 18 }} data-testid="v2-booknow-form">
      <h3 className="rec-h3" style={{ marginTop: 0 }}>Book {workspaceName}</h3>
      {settings && (
        <p className="m-muted small" style={{ marginBottom: 12 }}>
          {settings.buffer_before_minutes} min buffer before + {settings.buffer_after_minutes} min after each booking.
          Cancellations follow the host's policy at booking time.
        </p>
      )}
      <div className="m-form-grid-2">
        <label className="m-field"><span>Date</span>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            required data-testid="v2-booknow-date" />
        </label>
        <label className="m-field"><span>Start time</span>
          <select value={time} onChange={e => setTime(e.target.value)}
            required data-testid="v2-booknow-time"
            style={{ padding: "10px 14px", border: "1px solid var(--m-border)", borderRadius: 9, background: "var(--cream)", color: "var(--m-text)" }}>
            {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
      </div>
      {pricing && (
        (() => {
          const opts = [];
          if (pricing.hourly_enabled) opts.push({ k: "hourly", label: `Hourly · $${pricing.hourly_rate}/hr` });
          if (pricing.half_day_enabled) opts.push({ k: "half_day", label: `Half day (${pricing.half_day_duration_hours}h) · $${pricing.half_day_rate}` });
          if (pricing.full_day_enabled) opts.push({ k: "full_day", label: `Full day (${pricing.full_day_duration_hours}h) · $${pricing.full_day_rate}` });
          if (opts.length < 2) return null;  // only show picker if there's a real choice
          return (
            <label className="m-field" style={{ marginTop: 10 }}><span>Rate</span>
              <div className="m-amenities" data-testid="v2-booknow-pricing-type">
                {opts.map(o => (
                  <button key={o.k} type="button"
                    className={"m-pill light" + (pricingType === o.k ? " active" : "")}
                    onClick={() => setPricingType(o.k)}
                    data-testid={`v2-booknow-pricing-${o.k}`}>{o.label}</button>
                ))}
              </div>
            </label>
          );
        })()
      )}
      {pricingType === "hourly" && (
        <label className="m-field" style={{ marginTop: 10 }}><span>Duration</span>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {HOURS_OPTIONS.map(h => (
            <button type="button" key={h}
              className={"m-pill light" + (hours === h ? " active" : "")}
              onClick={() => setHours(h)}
              data-testid={`v2-booknow-hours-${h}`}>{h}h</button>
          ))}
        </div>
      </label>
      )}
      {pricingType !== "hourly" && pricing && (
        <div className="m-muted small" style={{ marginTop: 10 }} data-testid="v2-booknow-fixed-duration">
          Booking length is set by your host: <b>
            {pricingType === "half_day" ? pricing.half_day_duration_hours : pricing.full_day_duration_hours}h
          </b> starting at {time}.
        </div>
      )}
      <div className="m-booking-contact" style={{ marginTop: 22 }}>
        <div style={{
          letterSpacing: "0.16em", textTransform: "uppercase", fontSize: 10.5,
          color: "rgba(27,35,64,0.62)", fontWeight: 600, marginBottom: 10,
        }}>Booking Contact</div>
        <label className="m-field" style={{ display: "block", marginBottom: 12 }}>
          <span>Full name</span>
          <input type="text" value={name} onChange={e => setName(e.target.value)}
            autoComplete="name" required data-testid="v2-booknow-name"
            style={{ width: "100%" }} />
        </label>
        <label className="m-field" style={{ display: "block", marginBottom: 12 }}>
          <span>Email address</span>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
            autoComplete="email" required data-testid="v2-booknow-email"
            style={{ width: "100%" }} />
        </label>
        <label className="m-field" style={{ display: "block" }}>
          <span>Phone number <span style={{ color: "rgba(27,35,64,0.5)", fontWeight: 400 }}>(optional)</span></span>
          <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
            autoComplete="tel" placeholder="For day-of coordination"
            data-testid="v2-booknow-phone"
            style={{ width: "100%" }} />
        </label>
      </div>

      {estAmount != null && (
        <div style={{ marginTop: 14 }}>
          {depositTerms ? (
            (() => {
              const totalCents = Math.round(estAmount * 100);
              let depCents;
              if (depositTerms.deposit_type === "fixed" && depositTerms.deposit_amount) {
                depCents = Math.min(totalCents - 1, depositTerms.deposit_amount);
              } else {
                const pct = depositTerms.deposit_percentage || 25;
                depCents = Math.round(totalCents * (pct / 100));
              }
              if (depCents >= totalCents) depCents = totalCents - 1;
              const balCents = totalCents - depCents;
              const balanceLabel = (() => {
                if (depositTerms.balance_due_timing === "days_before_event") {
                  const d = depositTerms.balance_due_days_before || 1;
                  return `${d} day${d === 1 ? "" : "s"} before your event`;
                }
                return `on your event day at ${depositTerms.balance_due_time_local || "00:01"} (${depositTerms.venue_timezone || "local time"})`;
              })();
              return (
                <div style={{ padding: "14px 16px", background: "rgba(184,145,58,0.06)", border: "1px solid rgba(184,145,58,0.28)", borderRadius: 10, fontSize: 14 }} data-testid="v2-booknow-deposit-breakdown">
                  <div style={{ letterSpacing: "0.14em", textTransform: "uppercase", fontSize: 10.5, color: "rgba(27,35,64,0.62)", marginBottom: 8 }}>Payment schedule · deposit + balance</div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "rgba(27,35,64,0.75)" }}>Reservation total</span>
                    <b>${(totalCents / 100).toFixed(2)}</b>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
                    <span style={{ color: "rgba(27,35,64,0.75)" }}>Due today (deposit)</span>
                    <b data-testid="v2-booknow-deposit-amount">${(depCents / 100).toFixed(2)}</b>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5, borderTop: "1px solid rgba(27,35,64,0.08)", paddingTop: 6 }}>
                    <span style={{ color: "rgba(27,35,64,0.75)" }}>Remaining balance</span>
                    <b data-testid="v2-booknow-balance-amount">${(balCents / 100).toFixed(2)}</b>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.55, color: "rgba(27,35,64,0.72)" }}>
                    Balance is due {balanceLabel}.
                    {depositTerms.auto_charge_balance ? (
                      <> Your saved payment method will be charged automatically — no action needed from you.</>
                    ) : (
                      <> You'll receive an invoice on the balance due date.</>
                    )}
                  </div>
                </div>
              );
            })()
          ) : (
            <div style={{
              padding: "16px 18px",
              background: "linear-gradient(180deg, rgba(184,145,58,0.10), rgba(184,145,58,0.04))",
              border: "1px solid rgba(184,145,58,0.42)",
              borderRadius: 12,
              display: "flex", alignItems: "baseline", justifyContent: "space-between",
              gap: 14, flexWrap: "wrap",
            }} data-testid="v2-booknow-estimated-total">
              <div>
                <div style={{ fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(27,35,64,0.62)", fontWeight: 600, marginBottom: 2 }}>
                  Estimated total
                </div>
                <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 28, fontWeight: 500, lineHeight: 1, color: "#1B2340" }}>
                  ${estAmount.toFixed(2)}
                </div>
              </div>
              <div className="m-muted small" style={{ fontSize: 12, textAlign: "right", maxWidth: 180 }}>
                charged securely via Stripe
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="m-error small" style={{ marginTop: 10 }} data-testid="v2-booknow-error">
          {error}
        </div>
      )}

      <div className="m-form-actions" style={{ marginTop: 16 }}>
        <button type="submit" className="m-btn-gold" disabled={busy}
          data-testid="v2-booknow-submit">
          {busy ? "Redirecting to secure checkout…" : (depositTerms ? "Pay deposit & reserve →" : "Book now →")}
        </button>
      </div>
      <p className="m-muted small" style={{ marginTop: 10 }}>
        {depositTerms ? (
          <>You'll complete your deposit payment on Stripe's secure page. Your reservation is only confirmed after the deposit is verified. By continuing you authorize Métier to charge your saved payment method for the remaining balance on the balance due date.</>
        ) : (
          <>You'll complete payment on Stripe's secure page. Your booking is only confirmed after payment is verified.</>
        )}
      </p>
      <div className="legal-inline-ack" data-testid="v2-booknow-legal-ack">
        By completing this reservation you agree to the{" "}
        <a href="/legal/terms" target="_blank" rel="noopener">Métier Terms of Service</a>,{" "}
        <a href="/legal/privacy" target="_blank" rel="noopener">Privacy Policy</a>, and the venue's cancellation policy.
      </div>
    </form>
  );
}
